import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useInView } from "../components/useInView";
import "./Products.css";

/* ── Hero background ── */
import heroBg from "../assets/images/products/ppt_media_image1.jpg";

/* ── Intro section image ── */
import introImg from "../assets/images/products/ppt_media_image11.jpg";

/* ── Product images ── */
import img1 from "../assets/images/products/ppt_media_image1.jpg";
import img2 from "../assets/images/products/ppt_media_image2.jpg";
import img3 from "../assets/images/products/ppt_media_image3.jpg";
import img4 from "../assets/images/products/ppt_media_image4.jpg";
import img5 from "../assets/images/products/ppt_media_image5.jpg";
import img6 from "../assets/images/products/ppt_media_image6.jpg";
import img7 from "../assets/images/products/ppt_media_image7.jpg";
import img8 from "../assets/images/products/ppt_media_image8.jpg";

/* ── Product videos ── */
import video1 from "../assets/images/videos/landscape_video1.mp4";

/* ── Product data ── */
const categories = [
  {
    id: "walling",
    label: "Walling & Columns",
    tag: "WALLING & COLUMNS",
    tagline: "Formwork Solutions for Every Structure",
    desc:
      "Versatile wall and column formwork systems engineered for speed, adaptability, and a high-quality concrete finish — from simple rectangular forms to complex architectural profiles.",
    dark: false,
    products: [
      {
        name: "Minima",
        desc:
          "Modular wall formwork panels designed for concrete pressures up to 60 kN/m². Minima has the versatility to adapt to most building shapes quickly and easily. Available in a wide range of panel sizes, allowing virtually any configuration.",
        image: img1,
      },
      {
        name: "Airodek System",
        desc:
          "The Airodek system of lightweight aluminum panels and adjustable props means 30% less weight and 40% fewer parts than conventional steel props and timber, and 25% less weight than other systems using primary beams and panels. All of which saves time, labour and cost.",
        video: video1,
      },
      {
        name: "Superslim Soldiers with GTX & Plywood",
        desc:
          "When using a plywood form face together with KiT's GTX timber beams, Superslim Soldiers can be used to construct straight, curved, conical or other complex formwork configurations. Ideal for a high architectural finish.",
        image: img2,
      },
    ],
  },
  {
    id: "falsework",
    label: "Falsework / Shoring",
    tag: "FALSEWORK / SHORING",
    tagline: "Load-Bearing Systems Built for Heavy-Duty Performance",
    desc:
      "From light-duty modular shoring to heavy-lift propping towers, our falsework range delivers the load capacity, safety features and versatility your project demands.",
    dark: true,
    products: [
      {
        name: "Rapidshor",
        desc:
          "A cost-effective modular galvanised steel shoring system. High leg capacity up to 80 kN saves material and labour costs. Easy to erect with regular patterns of snap-on braces. Accommodates sloping ground and varying slab levels.",
        image: img3,
      },
      {
        name: "Alshor Plus",
        desc:
          "A high-performance aluminium shoring system with increased safety features, load capacity and variable ledger frame positions. Leg loading up to 120 kN/leg. Greater on-site versatility and cost effectiveness.",
        image: img4,
      },
      {
        name: "Megashor",
        desc:
          "A heavy-duty modular propping system for axial loads up to 750 kN. From heavy lifting towers to travellers for tunnel formwork, bracing for excavations, shoring and trusses — an extremely versatile system with varied configurations.",
        image: img5,
      },
    ],
  },
  {
    id: "decking",
    label: "Decking",
    tag: "DECKING",
    tagline: "Efficient Soffit & Slab Deck Systems",
    desc:
      "Lightweight yet robust decking solutions that reduce erection time, improve safety on site and integrate seamlessly with our full range of falsework products.",
    dark: false,
    products: [
      {
        name: "Airodek",
        desc:
          "A lightweight soffit formwork system, simple and fast to erect and dismantle. Designed for easy assembly, transportation, storage and maintenance. Can be used in conjunction with all of our falsework products.",
        image: img6,
      },
      {
        name: "Superslim Soldiers with GTX",
        desc:
          "A definitive formwork primary beam with an unrivalled strength-to-weight ratio, versatility and range of accessories. Robust and easily assembled into beams of almost any length. Can be re-used on varied sites and applications.",
        image: img7,
      },
      {
        name: "Kwikstage",
        desc:
          "One of the most universally established scaffolds throughout the world. Used for propping and decking applications. The simplicity makes Kwikstage a versatile, efficient and cost-effective alternative to traditional loose props.",
        image: img8,
      },
    ],
  },
];

/* ── FAQ / accordion data ── */
const faqs = [
  {
    title: "Equipment Availability",
    body: "Our customers are able to benefit from availability, flexible delivery and improved equipment storage. With dedicated formwork storage yards throughout South Africa, Bakwena Kit can respond quickly and easily to our customer's requirements. With access to the extensive regional network, our customers can be able to benefit from availability, flexible delivery and improved equipment storage. A high-quality fleet, delivered when it's needed, on time and in full.",
  },
  {
    title: "Customer Demonstrations and Training Facility",
    body: "We offer hands-on practical training. Bakwena Kit has a demonstration facility at our Head Office. We can provide our customers with a more detailed understanding of the benefits our equipment can provide. We also offer hands-on practical training in the safe and efficient use of our products, undertaken either at our demonstration facility or on our customer's site, ensuring they have all the knowledge they need to work safely with our equipment on site — on time and in full.",
  },
];

/* ── Accordion item ── */
function FaqItem({ item, index, open, onToggle }) {
  return (
    <div className={`pd-faq__item${open ? " pd-faq__item--open" : ""}`}>
      <button className="pd-faq__trigger" onClick={onToggle} type="button">
        <span className="pd-faq__num">0{index + 1}.</span>
        <span className="pd-faq__q">{item.title}</span>
        <span className="pd-faq__icon">
          {open ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
        </span>
      </button>
      <div className="pd-faq__body">
        <p>{item.body}</p>
      </div>
    </div>
  );
}

/* ── Video card with play button ── */
function VideoCard({ src, tag }) {
  const videoRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setPlaying(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  const handleEnded = () => setPlaying(false);

  return (
    <div className="pd-card__img-wrap pd-card__video-wrap">
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        draggable="false"
        onEnded={handleEnded}
        onDragStart={(e) => e.preventDefault()}
      />
      {!playing && (
        <button className="pd-video__play-btn" onClick={handlePlay} type="button" aria-label="Play video">
          <span className="pd-video__play-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </button>
      )}
      {playing && (
        <button className="pd-video__pause-btn" onClick={handlePause} type="button" aria-label="Pause video">
          <span className="pd-video__play-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
          </span>
        </button>
      )}
      <span className="pd-card__cat">{tag}</span>
    </div>
  );
}

/* ── disable right-click ── */
function useNoContextMenu() {
  React.useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);
}

/* ── Single category section ── */
function CategorySection({ cat, inView }) {
  return (
    <section
      id={cat.id}
      className={`pd-section${cat.dark ? " pd-section--dark" : ""}`}
    >
      <div className="container">
        <div className={`pd-section__head fade-in ${inView ? "visible" : ""}`}>
          <div className="pd-section__tag">{cat.tag}</div>
          <h2 className="pd-section__h2">
            {cat.tagline.split(" ").slice(0, -2).join(" ")}{" "}
            <span>{cat.tagline.split(" ").slice(-2).join(" ")}</span>
          </h2>
          <p className="pd-section__sub">{cat.desc}</p>
        </div>

        <div className="pd-grid">
          {cat.products.map((p, i) => (
            <div
              key={i}
              className={`pd-card fade-in ${inView ? "visible" : ""}`}
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              {p.video ? (
                <VideoCard src={p.video} tag={cat.tag} />
              ) : (
                <div className="pd-card__img-wrap">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    draggable="false"
                    onDragStart={(e) => e.preventDefault()}
                  />
                  <span className="pd-card__cat">{cat.tag}</span>
                </div>
              )}
              <div className="pd-card__body">
                <div className="pd-card__name">{p.name}</div>
                <p className="pd-card__desc">{p.desc}</p>
              </div>
              <div className="pd-card__footer">
                <span>Learn More</span>
                <span className="pd-card__footer-line" />
                <FiArrowRight size={14} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════ */
export default function Products() {
  useNoContextMenu();

  const [openFaq, setOpenFaq] = useState(0);
  const [heroRef, heroInView] = useInView();
  const [introRef, introInView] = useInView();
  const [sec0Ref, sec0InView] = useInView();
  const [sec1Ref, sec1InView] = useInView();
  const [sec2Ref, sec2InView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  const sectionRefs = [
    { ref: sec0Ref, inView: sec0InView },
    { ref: sec1Ref, inView: sec1InView },
    { ref: sec2Ref, inView: sec2InView },
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main>
      {/* ══════ 1. HERO ══════ */}
      <section
        className="pd-hero"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="pd-hero__overlay" />
        <div className="container pd-hero__body" ref={heroRef}>
          <div
            className={`pd-hero__label fade-in ${heroInView ? "visible" : ""}`}
          >
            OUR PRODUCTS
          </div>
          <h1
            className={`pd-hero__title fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.1s" }}
          >
            FORMWORK &amp;<br />SCAFFOLDING SYSTEMS
          </h1>
        </div>
        <div className="pd-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={13} />
          <Link to="/projects">Projects</Link>
          <FiArrowRight size={13} />
          <span>Products</span>
        </div>
      </section>

      {/* ══════ 2. WHAT WE SUPPLY — accordion + image grid ══════ */}
      <section className="pd-intro" ref={introRef}>
        <div className="container pd-intro__inner">

          {/* Left: heading + accordion */}
          <div className={`pd-intro__left fade-in ${introInView ? "visible" : ""}`}>
            <div className="pd-intro__tag">WHAT WE SUPPLY</div>
            <h2 className="pd-intro__h2">
              We Are Experienced<br /><span>Temporary Works</span> Solution
            </h2>
            <div className="pd-faq">
              {faqs.map((faq, i) => (
                <FaqItem
                  key={i}
                  item={faq}
                  index={i}
                  open={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? null : i)}
                />
              ))}
            </div>
          </div>

          {/* Right: single image */}
          <div className={`pd-intro__right fade-in ${introInView ? "visible" : ""}`} style={{ transitionDelay: "0.15s" }}>
            <div className="pd-intro__img-single">
              <img src={introImg} alt="Bakwena Kit formwork solutions" draggable="false" onDragStart={e => e.preventDefault()} />
            </div>
          </div>

        </div>
      </section>

      {/* ══════ 3. CATEGORY JUMP NAV ══════ */}
      <nav className="pd-cat-nav">
        <div className="container pd-cat-nav__inner">
          {categories.map((c) => (
            <button
              key={c.id}
              className="pd-cat-nav__btn"
              type="button"
              onClick={() => scrollTo(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </nav>

      {/* ══════ 4. CATEGORY SECTIONS ══════ */}
      {categories.map((cat, i) => (
        <React.Fragment key={cat.id}>
          {i > 0 && <div className="pd-strip" aria-hidden="true" />}
          <div ref={sectionRefs[i].ref}>
            <CategorySection cat={cat} inView={sectionRefs[i].inView} />
          </div>
        </React.Fragment>
      ))}

      {/* ══════ 5. CTA BAND ══════ */}
      <section className="pd-cta" ref={ctaRef}>
        <div className={`container pd-cta__inner fade-in ${ctaInView ? "visible" : ""}`}>
          <h2 className="pd-cta__h2">
            Ready to Discuss<br />
            <span>Your Next Project?</span>
          </h2>
          <div className="pd-cta__actions">
            <Link to="/contact" className="pd-cta__btn">
              Get a Quote <FiArrowRight size={14} />
            </Link>
            <Link to="/about" className="pd-cta__btn pd-cta__btn--ghost">
              About Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}